import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Clock, Trash } from '@phosphor-icons/react';
import { RegistrationRequest } from '@/types/auth';
import { toast } from 'sonner';

interface RegistrationManagerProps {
  currentAdminUsername: string;
}

export const RegistrationManager = ({ currentAdminUsername }: RegistrationManagerProps) => {
  const [registrations, setRegistrations] = useState<RegistrationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReg, setSelectedReg] = useState<RegistrationRequest | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchRegistrations = async () => {
    try {
      const response = await fetch('/api/auth/register');
      if (response.ok) {
        const data = await response.json();
        setRegistrations(data);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const handleApprove = async (reg: RegistrationRequest) => {
    if (!confirm(`Approve registration for ${reg.name} (${reg.username})?`)) {
      return;
    }

    setProcessingId(reg.id);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registrationId: reg.id,
          action: 'approve',
          reviewedBy: currentAdminUsername,
        }),
      });

      if (response.ok) {
        toast.success(`Registration approved for ${reg.name}`);
        await fetchRegistrations();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to approve registration');
      }
    } catch (error) {
      console.error('Error approving registration:', error);
      toast.error('An error occurred');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedReg || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessingId(selectedReg.id);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registrationId: selectedReg.id,
          action: 'reject',
          reviewedBy: currentAdminUsername,
          rejectionReason,
        }),
      });

      if (response.ok) {
        toast.success(`Registration rejected for ${selectedReg.name}`);
        setRejectDialogOpen(false);
        setRejectionReason('');
        setSelectedReg(null);
        await fetchRegistrations();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to reject registration');
      }
    } catch (error) {
      console.error('Error rejecting registration:', error);
      toast.error('An error occurred');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (reg: RegistrationRequest) => {
    if (!confirm(`Delete registration request for ${reg.name}?`)) {
      return;
    }

    setProcessingId(reg.id);
    try {
      const response = await fetch(`/api/auth/register?registrationId=${reg.id}`, {
        method: 'DELETE',
      });

      if (response.ok || response.status === 204) {
        toast.success('Registration deleted');
        await fetchRegistrations();
      } else {
        toast.error('Failed to delete registration');
      }
    } catch (error) {
      console.error('Error deleting registration:', error);
      toast.error('An error occurred');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getRoleDisplay = (role: string) => {
    const roleMap: Record<string, string> = {
      'boxer': 'Boxer',
      'sponsor': 'Sponsor',
      'staff': 'Staff',
      '1068-staff': '1068 Staff',
    };
    return roleMap[role] || role;
  };

  const pendingCount = registrations.filter(r => r.status === 'pending').length;

  if (loading) {
    return <div className="p-8 text-center">Loading registrations...</div>;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Registration Requests</CardTitle>
          <CardDescription>
            Review and approve account registration requests
            {pendingCount > 0 && (
              <span className="ml-2 text-yellow-600 font-semibold">
                ({pendingCount} pending)
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {registrations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No registration requests
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>State ID</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registrations.map((reg) => (
                    <TableRow key={reg.id}>
                      <TableCell>{getStatusBadge(reg.status)}</TableCell>
                      <TableCell className="font-medium">{reg.name}</TableCell>
                      <TableCell>{reg.username}</TableCell>
                      <TableCell>{reg.stateId}</TableCell>
                      <TableCell>{getRoleDisplay(reg.role)}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {new Date(reg.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {reg.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(reg)}
                              disabled={processingId === reg.id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedReg(reg);
                                setRejectDialogOpen(true);
                              }}
                              disabled={processingId === reg.id}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                        {reg.status !== 'pending' && (
                          <div className="flex gap-2">
                            <span className="text-sm text-gray-500">
                              {reg.reviewedBy && `By: ${reg.reviewedBy}`}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(reg)}
                              disabled={processingId === reg.id}
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Registration</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting {selectedReg?.name}'s registration request
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Textarea
              id="reason"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectionReason('');
                setSelectedReg(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectionReason.trim()}
            >
              Reject Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
